import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DialogActions,
  DialogContent,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Intestazione from './Intestazione';
import useStili from './stili';

const useStyles = makeStyles((theme) => ({
  intestazioneElenco: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.neutral.light,
    fontWeight: theme.typography.fontWeightMedium,
  },
  elenco: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  difetto: {
    padding: theme.spacing(1.5, 2),
    '& + &': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
}));

const ETICHETTA_COPIA = {
  fatta: 'Dettaglio copiato',
  fallita: 'Copia non riuscita',
};

// What the operator pastes into a message or back into HACCP Agent: the file
// and every defect, one per line.
const dettaglioErrore = (file, errori) =>
  [
    `File: ${file.name}`,
    ...errori.map(({ tipo, descrizione }) => `${tipo}: ${descrizione}`),
  ].join('\n');

/**
 * The check refused the file: what is wrong with it, element by element, so
 * the operator can fix it in HACCP Agent without opening it.
 */
const FileNonConforme = ({ file, errori, onCaricaAltroFile, onChiudi }) => {
  const stili = useStili();
  const classes = useStyles();
  const [copia, setCopia] = useState(null);

  // `navigator.clipboard` is missing outside a secure context, and the call
  // then throws instead of rejecting: both end in «non riuscita».
  const copiaDettaglio = () =>
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(dettaglioErrore(file, errori)))
      .then(
        () => setCopia('fatta'),
        () => setCopia('fallita'),
      );

  return (
    <>
      <DialogContent className={stili.content}>
        <Intestazione
          tono="errore"
          titolo="Import non eseguito: il file non è conforme"
          testo="La Gestione Operativa non è stata modificata. Correggi in HACCP Agent e riesporta il file."
        />
        <div className={stili.riquadro}>
          <Typography variant="body2" className={classes.intestazioneElenco}>
            {errori.length === 1
              ? '1 elemento in difetto'
              : `${errori.length} elementi in difetto`}
          </Typography>
          <ul className={classes.elenco}>
            {/* The list comes from one check and never changes while shown:
                the position is a stable key. */}
            {errori.map(({ tipo, descrizione }, indice) => (
              <li key={indice} className={classes.difetto}>
                <Typography variant="subtitle2" component="p">
                  {tipo}
                </Typography>
                <Typography variant="body2" className={stili.testo}>
                  {descrizione}
                </Typography>
              </li>
            ))}
          </ul>
        </div>
      </DialogContent>
      <DialogActions disableSpacing className={stili.actions}>
        <Button
          variant="outlined"
          className={`${stili.secondario} ${stili.spinta}`}
          onClick={copiaDettaglio}
        >
          {ETICHETTA_COPIA[copia] || "Copia il dettaglio dell'errore"}
        </Button>
        <Button
          variant="outlined"
          className={stili.secondario}
          onClick={onChiudi}
        >
          Chiudi
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={stili.primario}
          onClick={onCaricaAltroFile}
        >
          Carica un altro file
        </Button>
      </DialogActions>
    </>
  );
};

FileNonConforme.propTypes = {
  file: PropTypes.shape({ name: PropTypes.string.isRequired }).isRequired,
  errori: PropTypes.arrayOf(
    PropTypes.shape({
      tipo: PropTypes.string.isRequired,
      descrizione: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onCaricaAltroFile: PropTypes.func.isRequired,
  onChiudi: PropTypes.func.isRequired,
};

export default FileNonConforme;
